import React, { useEffect, useMemo, useState } from 'react';
import { Form, Spin } from '@duxton-web/ui';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import { styled } from 'styled-components';

import { ExperimentFormMode } from '.';
import Service from '../../lib/App';
import { Experiment, Variable } from '../../lib/PlannerDTOs';
import RestrictedEditingWrapper from './common/RestrictedEditingWrapper';
import {
  useTreatments,
  useVariables,
} from './contexts/ExperimentFormInfoProvider';
import {
  SELECTED_EXPERIMENT_LS_KEY,
  TreatmentDefaultColors,
  TreatmentIds,
} from './helper/consts';
import { convertExperimentPayloadToExperimentFormData } from './helper/experiment';
import {
  getRestrictedEditingTooltip,
  isExperimentInRestrictedEditState,
  scrollToMetricsSection,
} from './helper/restrictedEditing';
import Estimate from './Sections/Estimate';
import Header from './Sections/Header';
import JsonPreview from './Sections/JsonPreview';
import Metrics from './Sections/Metrics';
import Treatment from './Sections/Treatment';
import { REQUESTED, RUNNING, SCHEDULED, STARTED } from '@/constants';
import { error as logError } from '@/utils/logging';
import { getSearchParamsObject } from '@/utils/url';
import { getLoggedUserEmail, isAdmin } from '@/utils/user';

export const extractTreatmentData = (experiment: Experiment) => {
  const treatmentCount = Math.max(
    ...(experiment?.variables?.map((variable) => variable.choices.length) || [
      0,
    ]),
  );

  const treatmentIndexes = Array.from({ length: treatmentCount }, (_, i) => i);

  return treatmentIndexes.map((index) => {
    const treatmentId = TreatmentIds[index];
    return {
      id: treatmentId,
      name:
        treatmentId === 'Control' ? treatmentId : `Treatment ${treatmentId}`,
      color: TreatmentDefaultColors[treatmentId],
    };
  });
};

const ExperimentFormV2 = () => {
  const location = useLocation();
  const [form] = Form.useForm();
  const parsedParams = getSearchParamsObject(location.search);
  const [variables, setVariables] = useVariables();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [treatments, setTreatments] = useTreatments();
  const [selectedExperiment] = useLocalStorage<Experiment>(
    SELECTED_EXPERIMENT_LS_KEY,
  );
  const userEmail = getLoggedUserEmail();
  const [isLoadingVariables, setIsLoadingVariables] = useState(false);
  const [analysisData, setAnalysisData] = useState<any>(null);

  const extractVariablesName = (variables: Variable[]) => {
    return variables?.map((variable) => variable.name) || [];
  };

  const selectedExperimentMatchWithParams =
    !!selectedExperiment &&
    !!parsedParams.expName &&
    selectedExperiment.name === parsedParams.expName;

  const mode = (() => {
    if (
      selectedExperimentMatchWithParams &&
      Object.values(ExperimentFormMode).includes(parsedParams.action)
    ) {
      return parsedParams.action;
    }

    return ExperimentFormMode.CREATE;
  })();

  const isCloneMode = mode === ExperimentFormMode.CLONE;
  const isEditMode = mode === ExperimentFormMode.EDIT;
  const isCreateMode = mode === ExperimentFormMode.CREATE;

  const isRestrictedEditMode =
    isEditMode && isExperimentInRestrictedEditState(selectedExperiment);

  const restrictedEditingTooltip =
    getRestrictedEditingTooltip(selectedExperiment);

  const experimentStartMoreThan7Days = dayjs
    .unix(selectedExperiment?.startTime)
    .isBefore(dayjs().subtract(7, 'day').startOf('day'));

  const wrapRestricted = (children: React.ReactNode) => (
    <RestrictedEditingWrapper
      isRestricted={isRestrictedEditMode}
      tooltipMessage={restrictedEditingTooltip}
    >
      {children}
    </RestrictedEditingWrapper>
  );

  const metricIsRestricted = () => {
    if (isEditMode) {
      if (selectedExperiment?.state === REQUESTED) return false;

      if (![SCHEDULED, STARTED, RUNNING].includes(selectedExperiment?.state))
        return true;

      if (experimentStartMoreThan7Days && !isAdmin()) return true;

      if (analysisData?.AnalysisV2?.schemaVersion !== 4) return true;

      return false;
    }
  };

  // populate variables
  useEffect(() => {
    const fetchVariables = async (nameList: string[]) => {
      if (nameList.length > 0) {
        setIsLoadingVariables(true);
        const variableData = await Promise.all(
          nameList.map((name) => Service.planner.fetchVariable(name)),
        );
        setVariables(variableData);
        setIsLoadingVariables(false);
      }
    };
    if (isCreateMode) return;
    if (!selectedExperiment) return;
    const variableNames = extractVariablesName(selectedExperiment?.variables);
    fetchVariables(variableNames);
  }, [isCreateMode, selectedExperiment]);

  useEffect(() => {
    const fetchAnalysisData = async () => {
      if (!selectedExperiment?.name) return;

      try {
        const analysisResponse = await Service.planner!.fetchAnalysisConfig(
          selectedExperiment?.name,
        );
        setAnalysisData(analysisResponse);
      } catch (error) {
        logError(error, 'Failed to fetch analysis config', {
          experimentName: selectedExperiment.name,
        });
      }
    };

    fetchAnalysisData();
  }, [selectedExperiment?.name]);

  // populate treatments
  useEffect(() => {
    if (isCreateMode) return;
    if (!selectedExperiment) return;
    const treatmentData = extractTreatmentData(selectedExperiment);
    setTreatments(treatmentData);
  }, [isCreateMode, selectedExperiment]);

  // Auto-scroll to metrics section when in restricted edit mode
  useEffect(() => {
    if (isRestrictedEditMode) {
      scrollToMetricsSection();
    }
  }, [isRestrictedEditMode]);

  const defaultValues = useMemo(() => {
    if (isCreateMode) return undefined;
    if (!selectedExperiment) return undefined;
    if (!variables.length) return undefined;
    let convertedValues = convertExperimentPayloadToExperimentFormData(
      selectedExperiment,
      variables,
    );
    if (isCloneMode) {
      convertedValues = {
        ...convertedValues,
        owner: userEmail,
      };
    }
    return convertedValues;
  }, [isCloneMode, isCreateMode, selectedExperiment, variables]);

  const experimentName = useMemo(() => {
    if (isCreateMode || !selectedExperiment?.domain) return '';

    if (isEditMode) return selectedExperiment.name;

    if (isCloneMode && selectedExperiment.name) {
      // if name has domain as prefix, extract the name only
      let expName = '';
      const splittedName = selectedExperiment.name.split('.');
      if (
        splittedName.length > 1 &&
        splittedName[0] === selectedExperiment.domain
      ) {
        expName = splittedName[1] || '';
      } else {
        expName = selectedExperiment.name || '';
      }
      // remove the "clone_of_" if applicable
      expName = expName.startsWith('clone_of_')
        ? expName.replace('clone_of_', '')
        : expName;
      return `${selectedExperiment.domain}.${expName}`;
    }
    return '';
  }, [isCreateMode, isEditMode, selectedExperiment]);

  if ((isEditMode || isCloneMode) && isLoadingVariables) {
    return (
      <div
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Form
      layout="vertical"
      form={form}
      initialValues={defaultValues}
      style={{
        height: '100%',
      }}
    >
      <FormWrapper className="experimentPageV2-subPage">
        <Header />
        <FormBody className="form-body">
          <FormSectionWrapper>
            {wrapRestricted(<Treatment mode={mode} />)}
            {wrapRestricted(<Estimate />)}
            <RestrictedEditingWrapper
              isRestricted={metricIsRestricted()}
              tooltipMessage={
                experimentStartMoreThan7Days
                  ? 'You can only edit the metric of the experiment within 7 days of experiment start'
                  : restrictedEditingTooltip
              }
            >
              <Metrics experimentName={experimentName} />
            </RestrictedEditingWrapper>

            {wrapRestricted(<JsonPreview />)}
          </FormSectionWrapper>
        </FormBody>
      </FormWrapper>
    </Form>
  );
};

export const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

export const FormBody = styled.div`
  flex-grow: 1;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 24px;
`;

export const FormSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  isolation: isolate;
  z-index: 1;
`;

export default ExperimentFormV2;
